import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle, AlertTriangle, Archive, ArchiveRestore, CalendarPlus, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { AvatarStack, VotingAvatarStack } from "../../../components/ui/voter-avatars";
import { AnonymousVoterPlaceholders } from "../../../components/ui/anonymous-voter-placeholders";
import { buildColorMap } from "../../../components/ui/voter-avatar-utils";
import { useUserProfiles } from "../../../hooks/useUserProfiles";
import { useSafeNavigate } from "../../../hooks/useSafeNavigate";
import { resolveSessionCalendarAffordance } from "../../../lib/google-calendar";
import { SessionCalendarControl } from "../../../components/session-calendar-control";
import { normalizeEmail } from "../../../lib/utils";
import { getVoteTally } from "../../../lib/vote-tally";
import { getSelfMatchKeys } from "../../../lib/anonymous-voter-identities";
import { PollStatusMeta } from "../../../components/poll-status-meta";
import { SessionVotingDeadlineStatus } from "../../../components/session-voting-deadline-status";
import { pendingToneClass, votedToneClass } from "../../../lib/poll-status-tone";
import {
  isSchedulerVotingOpen,
  resolveSchedulerVotingDeadline,
} from "../../../lib/voting-deadlines";
import {
  anonymizeUsers,
  buildAnonymousIdentityMap,
} from "../../../lib/anonymous-voter-identities";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { useProfileInteraction } from "../../../hooks/useProfileInteraction";
import { ProfileInteractionModals } from "../../../components/ProfileInteractionModals";

// Strong-key self match (mirrors the foundation's anonymizeUsers self-exempt).
function isSelfUser(user, currentUser) {
  if (!currentUser) return false;
  const selfKeys = getSelfMatchKeys(currentUser);
  if (selfKeys.size === 0) return false;
  const userKeys = getSelfMatchKeys(user);
  for (const key of userKeys) {
    if (selfKeys.has(key)) return true;
  }
  return false;
}

export function SessionCard({
  scheduler,
  isArchived = false,
  canManage = false,
  archiveBusy = false,
  groupColor = null,
  showVoteNeeded = false,
  winningSlot = null,
  slots = [],
  participants = [],
  voters = [],
  // Layer-A aggregate counters (denormalized; written by the scheduler trigger).
  // OPTIONAL with Number.isFinite fallbacks so a call site that omits them
  // degrades to today's own-only behavior (no crash, no new leak). The avatar
  // STACK beside the count stays gated/own-only/redacted — only the NUMERIC
  // count switches to the authoritative counter (design §5).
  submittedVoteCount = null,
  totalParticipantCount = null,
  attendanceSummary = null,
  conflictsWith = [],
  questingGroup = null,
  displayTimeZone = null,
  showTimeZone = true,
  voteIdentityDisplayMode = "named",
  showVoterIdentities = true,
  currentUser = null,
  viewer = {},
  onArchiveToggle,
  onEditPoll,
  onDeletePoll,
}) {
  const safeNavigate = useSafeNavigate();
  const cardTitle = scheduler?.title || "Untitled poll";
  const votingDeadline = resolveSchedulerVotingDeadline(scheduler);
  const votingDeadlineMs = votingDeadline?.getTime?.() ?? null;
  const [deadlineTick, setDeadlineTick] = useState(0);
  useEffect(() => {
    if (!Number.isFinite(votingDeadlineMs)) return undefined;
    const remainingMs = votingDeadlineMs - Date.now();
    if (remainingMs <= 0) return undefined;
    const timer = window.setTimeout(
      () => setDeadlineTick((current) => current + 1),
      Math.min(remainingMs + 25, 2_147_000_000)
    );
    return () => window.clearTimeout(timer);
  }, [deadlineTick, votingDeadlineMs]);
  // eslint-disable-next-line react-hooks/purity -- boundary timer forces the live transition
  const votingNow = Date.now();
  const votingOpen = isSchedulerVotingOpen(scheduler, votingNow);
  const effectiveVoteIdentityDisplayMode = voteIdentityDisplayMode || (showVoterIdentities ? "named" : "hidden");
  const shouldShowVoteIdentities = effectiveVoteIdentityDisplayMode !== "hidden";
  const isAnonymous = effectiveVoteIdentityDisplayMode === "anonymous";
  const isHidden = effectiveVoteIdentityDisplayMode === "hidden";
  const participantEmails = participants.map((p) => (typeof p === "string" ? p : p.email));
  const voterEmails = voters.map((v) => normalizeEmail(v.email)).filter(Boolean);
  // Drop the real-email color map in hidden AND anonymous mode. In anonymous
  // mode the rendered stacks carry alias emails (which miss the map), so this
  // is dead weight today and a future-leak risk if alias emails ever coincided
  // with reals. Stacks still get per-bubble fallback colors via getColorForEmail.
  const colorMap = isHidden || isAnonymous ? {} : buildColorMap(participantEmails);
  const { enrichUsers } = useUserProfiles(participantEmails);
  const participantUsers = enrichUsers(participantEmails);
  const confirmedUsers = useMemo(
    () => enrichUsers(attendanceSummary?.confirmed || []),
    [attendanceSummary?.confirmed, enrichUsers]
  );
  const maybeUsers = useMemo(
    () => enrichUsers(attendanceSummary?.maybe || []),
    [attendanceSummary?.maybe, enrichUsers]
  );
  const unavailableUsers = useMemo(
    () => enrichUsers(attendanceSummary?.unavailable || []),
    [attendanceSummary?.unavailable, enrichUsers]
  );
  const unrespondedUsers = useMemo(
    () => enrichUsers(attendanceSummary?.unresponded || []),
    [attendanceSummary?.unresponded, enrichUsers]
  );
  const anonymousIdentityMap = useMemo(() => {
    if (effectiveVoteIdentityDisplayMode !== "anonymous") return null;
    return buildAnonymousIdentityMap(
      [...participantUsers, ...confirmedUsers, ...maybeUsers, ...unavailableUsers, ...unrespondedUsers],
      { scopeKey: `dashboard:scheduler:${scheduler.id}` }
    );
  }, [
    confirmedUsers,
    effectiveVoteIdentityDisplayMode,
    maybeUsers,
    participantUsers,
    scheduler.id,
    unavailableUsers,
    unrespondedUsers,
  ]);
  const participantUsersForDisplay = useMemo(() => {
    if (effectiveVoteIdentityDisplayMode !== "anonymous") return participantUsers;
    return anonymizeUsers(participantUsers, anonymousIdentityMap, { keyPrefix: "participants", currentUser });
  }, [anonymousIdentityMap, currentUser, effectiveVoteIdentityDisplayMode, participantUsers]);
  const confirmedUsersForDisplay = useMemo(() => {
    if (effectiveVoteIdentityDisplayMode !== "anonymous") return confirmedUsers;
    return anonymizeUsers(confirmedUsers, anonymousIdentityMap, { keyPrefix: "confirmed", currentUser });
  }, [anonymousIdentityMap, confirmedUsers, currentUser, effectiveVoteIdentityDisplayMode]);
  const maybeUsersForDisplay = useMemo(() => {
    if (effectiveVoteIdentityDisplayMode !== "anonymous") return maybeUsers;
    return anonymizeUsers(maybeUsers, anonymousIdentityMap, { keyPrefix: "maybe", currentUser });
  }, [anonymousIdentityMap, currentUser, effectiveVoteIdentityDisplayMode, maybeUsers]);
  const unavailableUsersForDisplay = useMemo(() => {
    if (effectiveVoteIdentityDisplayMode !== "anonymous") return unavailableUsers;
    return anonymizeUsers(unavailableUsers, anonymousIdentityMap, { keyPrefix: "unavailable", currentUser });
  }, [anonymousIdentityMap, currentUser, effectiveVoteIdentityDisplayMode, unavailableUsers]);
  const unrespondedUsersForDisplay = useMemo(() => {
    if (effectiveVoteIdentityDisplayMode !== "anonymous") return unrespondedUsers;
    return anonymizeUsers(unrespondedUsers, anonymousIdentityMap, { keyPrefix: "unresponded", currentUser });
  }, [anonymousIdentityMap, currentUser, effectiveVoteIdentityDisplayMode, unrespondedUsers]);
  // Calculate who has voted and who hasn't
  const voterEmailSet = new Set(voterEmails);
  const pendingVoters = participantUsers.filter(
    (p) => !voterEmailSet.has(normalizeEmail(p.email))
  );
  const pendingVotersForDisplay = useMemo(() => {
    if (effectiveVoteIdentityDisplayMode !== "anonymous") return pendingVoters;
    return anonymizeUsers(pendingVoters, anonymousIdentityMap, { keyPrefix: "pending", currentUser });
  }, [anonymousIdentityMap, currentUser, effectiveVoteIdentityDisplayMode, pendingVoters]);

  // Determine guests (participants not in questing group)
  const groupMemberSet = new Set(
    (questingGroup?.members || []).map((email) => normalizeEmail(email)).filter(Boolean)
  );
  const guestCount = participantEmails.filter(
    (email) => !groupMemberSet.has(normalizeEmail(email))
  ).length;

  const totalParticipants = participantUsers.length;
  const hasParticipants = totalParticipants > 0;
  // Layer-A aggregate counts (design §5): the COUNT must come from the
  // denormalized counters, never the own-only fetched-doc length. Under
  // restrictive privacy `voters` holds only the viewer's own doc, so the
  // own-only split would report a misleading "0 voted / N pending".
  // The counters are authoritative when present.
  const hasCounters = Number.isFinite(submittedVoteCount);
  // PR-5: route the aggregate count through the centralized guest-aware tally.
  // Feed it the denormalized counters (the prop submittedVoteCount is the
  // guest-INCLUSIVE total alias; participantSubmittedVoteCount/votesAllSubmitted
  // ride on the scheduler doc). The card renders only the PENDING count + "All
  // voted!", and pending is participant-based (guests never inflate it).
  const sessionTally = getVoteTally({
    totalSubmittedVoteCount: submittedVoteCount,
    participantSubmittedVoteCount: scheduler.participantSubmittedVoteCount,
    totalParticipantCount,
    votesAllSubmitted: scheduler.votesAllSubmitted,
  });
  const aggregateParticipantCount =
    Number.isFinite(totalParticipantCount) && totalParticipantCount > 0
      ? sessionTally.participantTotal
      : participantUsers.length;
  // When the counters are absent, fall back to the SET-BASED pendingVoters
  // (participants whose email isn't in voterEmailSet) — byte-equivalent to the
  // pre-PR4 behavior — so the rendered number stays consistent with the
  // pendingVotersForDisplay STACK (which is also set-based). Computing pending
  // as `participantCount - voters.length` would diverge from the stack when a
  // vote doc's email doesn't match any participant (removed participant /
  // normalization skew). The card renders only the pending count + "All
  // voted!", so a separate voted count isn't needed.
  const aggregatePending = hasCounters ? sessionTally.pendingCount : pendingVoters.length;
  // "All votes in" prefers the trigger's participant-only completion gate
  // (votesAllSubmitted) / the aggregate, not the own-only pendingVoters list.
  const allVotesIn =
    scheduler.status === "OPEN" &&
    hasParticipants &&
    (scheduler.votesAllSubmitted === true || aggregatePending === 0);
  // Avatar click → profile interaction
  const { handleAvatarClick, modalProps } = useProfileInteraction();
  const avatarOnClick = isAnonymous ? undefined : handleAvatarClick;

  // Unified add-to-calendar prominence (design §4.1). The pure selector decides
  // ONE prominent affordance (Open when on-list + linked; else Add) from stored
  // data + the viewer's own settings — no Google API call. The de-emphasized
  // "Add (again)" is hosted in the card kebab (creators) or the control's own
  // muted-secondary button (non-creator invitees).
  const [calAddOpen, setCalAddOpen] = useState(false);
  const calAff = resolveSessionCalendarAffordance(
    scheduler,
    currentUser?.uid,
    winningSlot,
    viewer,
  );

  // Guard: when the dropdown menu closes, the browser may retarget the click
  // event to the card underneath (portal removed mid-click). This ref prevents
  // the card from navigating when the menu just closed.
  const menuJustClosedRef = useRef(false);
  const handleMenuOpenChange = useCallback((open) => {
    if (!open) {
      menuJustClosedRef.current = true;
      requestAnimationFrame(() => { menuJustClosedRef.current = false; });
    }
  }, []);

  const handleOpen = (event) => {
    if (menuJustClosedRef.current) return;
    // React portals bubble synthetic events through the component tree.
    // Ignore events that originated inside a dropdown menu portal.
    if (event?.target?.closest?.('[role="menu"], [role="menuitem"]')) return;
    const target = `/scheduler/${scheduler.id}`;
    safeNavigate(target);
  };

  const hasActions = Boolean(onArchiveToggle || (canManage && (onEditPoll || onDeletePoll)));

  const handleKeyDown = (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    // Ignore events from inside a dropdown menu portal.
    if (event?.target?.closest?.('[role="menu"], [role="menuitem"]')) return;
    event.preventDefault();
    handleOpen(event);
  };

  return (
    <>
      <article
        role="button"
        tabIndex={0}
        onClick={handleOpen}
        onKeyDown={handleKeyDown}
        aria-label={cardTitle}
        data-testid={`session-card-${scheduler?.id || "unknown"}`}
        className="relative flex w-full flex-col gap-2 rounded-2xl border border-slate-200/70 bg-white px-4 py-3 text-left transition-all duration-150 motion-safe:hover:scale-[1.02] hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:border-slate-700 dark:bg-slate-800"
        style={{
          borderLeftWidth: groupColor ? "4px" : undefined,
          borderLeftColor: groupColor || undefined,
        }}
      >
        {/* Title Row */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                {cardTitle}
              </p>
              {showVoteNeeded && votingOpen && (
                <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                  <AlertCircle className="h-3 w-3" />
                  Needs vote
                </span>
              )}
              {conflictsWith.length > 0 && (
                <span
                  className="flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/50 dark:text-amber-300"
                  title={`Conflicts with: ${conflictsWith.join(", ")}`}
                >
                  <AlertTriangle className="h-3 w-3" />
                  Conflict
                </span>
              )}
            </div>

            <PollStatusMeta
              scheduler={scheduler}
              winningSlot={winningSlot}
              slots={slots}
              allVotesIn={allVotesIn}
              isArchived={isArchived}
              questingGroupName={questingGroup?.name || null}
              questingGroupColor={groupColor}
              guestCount={guestCount}
              displayTimeZone={displayTimeZone}
              showTimeZone={showTimeZone}
            />
            <SessionVotingDeadlineStatus
              compact
              scheduler={scheduler}
              now={votingNow}
              displayTimeZone={displayTimeZone}
              showTimeZone={showTimeZone}
            />
          </div>

          <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-1">
            {/* Unified add-to-calendar control (one prominent affordance). The
                de-emphasized "Add (again)" is hosted in the kebab for creators
                (kebabHosted) and as a muted-secondary button otherwise. */}
            <SessionCalendarControl
              scheduler={scheduler}
              viewerUid={currentUser?.uid}
              slot={winningSlot}
              viewer={viewer}
              filenameBase={scheduler?.id || "session"}
              variant="card-icon"
              addOpen={calAddOpen}
              onAddOpenChange={setCalAddOpen}
              kebabHosted={canManage && hasActions}
            />

            {/* Creator kebab menu */}
            {canManage && hasActions ? (
              <DropdownMenu onOpenChange={handleMenuOpenChange}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    onClick={(event) => event.stopPropagation()}
                    disabled={archiveBusy}
                    className="rounded-full border border-slate-200 p-2 text-slate-500 transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-700"
                    aria-label="Session poll actions"
                    title="Session poll actions"
                  >
                    <MoreVertical className="h-5 w-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  {calAff.mode === "open" && calAff.entry ? (
                    <DropdownMenuItem onSelect={() => setCalAddOpen(true)}>
                      <CalendarPlus className="mr-2 h-4 w-4" />
                      Add to calendar (again)
                    </DropdownMenuItem>
                  ) : null}
                  {onArchiveToggle ? (
                    <DropdownMenuItem
                      onSelect={onArchiveToggle}
                      disabled={archiveBusy}
                    >
                      {isArchived ? <ArchiveRestore className="mr-2 h-4 w-4" /> : <Archive className="mr-2 h-4 w-4" />}
                      {isArchived ? "Unarchive" : "Archive"}
                    </DropdownMenuItem>
                  ) : null}
                  {onEditPoll ? (
                    <DropdownMenuItem onSelect={onEditPoll}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                  ) : null}
                  {onDeletePoll ? (
                    <DropdownMenuItem
                      onSelect={onDeletePoll}
                      className="text-rose-600 dark:text-rose-400"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}

            {/* Participant archive button (non-creator) */}
            {!canManage && onArchiveToggle ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onArchiveToggle();
                }}
                disabled={archiveBusy}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-700"
                aria-label={isArchived ? "Unarchive session" : "Archive session"}
                title={isArchived ? "Unarchive session" : "Archive session"}
              >
                {isArchived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
              </button>
            ) : null}
          </div>
        </div>

        {/* Participants Row - only show if we have participants */}
        {participantUsers.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
            {/* Invitees section */}
            <div className="flex items-center gap-1.5">
              <span className="font-medium">{participantUsers.length} invitee{participantUsers.length !== 1 ? "s" : ""}:</span>
              {shouldShowVoteIdentities ? (
                <AvatarStack
                  users={participantUsersForDisplay}
                  max={10}
                  size={18}
                  colorMap={colorMap}
                  onClick={avatarOnClick}
                />
              ) : isHidden ? (
                <AnonymousVoterPlaceholders
                  count={participantUsers.filter((p) => !isSelfUser(p, currentUser)).length}
                  selfUser={
                    currentUser && participantUsers.some((p) => isSelfUser(p, currentUser))
                      ? currentUser
                      : null
                  }
                />
              ) : null}
            </div>

            {/* Pending votes section - only show for open polls. The COUNT is
                Layer A (authoritative aggregate); the avatar STACK stays
                own-only / gated / redacted (only rendered when identities are
                visible AND the local pending list is non-empty). */}
            {votingOpen && hasParticipants && (
              <div className="flex items-center gap-1.5">
                {aggregatePending > 0 ? (
                  <>
                    <span className={`font-medium ${pendingToneClass}`}>
                      {aggregatePending}/{aggregateParticipantCount} pending:
                    </span>
                    {shouldShowVoteIdentities && pendingVoters.length > 0 ? (
                      <VotingAvatarStack users={pendingVotersForDisplay} size={18} colorMap={colorMap} onClick={avatarOnClick} />
                    ) : null}
                  </>
                ) : (
                  <span className={votedToneClass}>All voted!</span>
                )}
              </div>
            )}
          </div>
        )}

        {scheduler.status === "FINALIZED" && attendanceSummary && (
          <div className="mt-2 grid w-full gap-2 rounded-xl border border-slate-200/70 bg-slate-50 px-3 py-2 text-[11px] text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-emerald-600 dark:text-emerald-300">
                Confirmed
              </span>
              {shouldShowVoteIdentities ? (
                <VotingAvatarStack users={confirmedUsersForDisplay} size={16} colorMap={colorMap} onClick={avatarOnClick} />
              ) : null}
              <span className="text-slate-400 dark:text-slate-500">
                {confirmedUsers.length}
              </span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-amber-600 dark:text-amber-300">
                Maybe
              </span>
              {shouldShowVoteIdentities ? (
                <VotingAvatarStack users={maybeUsersForDisplay} size={16} colorMap={colorMap} onClick={avatarOnClick} />
              ) : null}
              <span className="text-slate-400 dark:text-slate-500">
                {maybeUsers.length}
              </span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-rose-600 dark:text-rose-300">
                Unavailable
              </span>
              {shouldShowVoteIdentities ? (
                <VotingAvatarStack users={unavailableUsersForDisplay} size={16} colorMap={colorMap} onClick={avatarOnClick} />
              ) : null}
              <span className="text-slate-400 dark:text-slate-500">
                {unavailableUsers.length}
              </span>
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-slate-500 dark:text-slate-300">
                Unresponded
              </span>
              {shouldShowVoteIdentities ? (
                <VotingAvatarStack users={unrespondedUsersForDisplay} size={16} colorMap={colorMap} onClick={avatarOnClick} />
              ) : null}
              <span className="text-slate-400 dark:text-slate-500">
                {unrespondedUsers.length}
              </span>
            </div>
          </div>
        )}
      </article>

      <ProfileInteractionModals {...modalProps} />
    </>
  );
}
